// User guide. Every section and block may list `roles`; a reader sees only what their role can do.
// Media files live in public/docs and are produced by tools/docs/capture.py.
export const ROLE_LABELS = {
  admin: "Администратор",
  owner: "Владелец",
  manager: "Руководитель",
  executor: "Исполнитель",
};

const ALL = ["admin", "owner", "manager", "executor"];
const MANAGERS = ["admin", "owner", "manager"];
const DIRECTORS = ["admin", "owner"];

export const DOC_SECTIONS = [
  {
    id: "start",
    title: "Начало работы",
    roles: ALL,
    blocks: [
      { type: "p", text: "Tracker — рабочий трекер задач. В нём ставят задачи, назначают исполнителей и сроки, обсуждают работу в чате задачи и отмечают результат" },
      { type: "image", src: "login.webp", caption: "Экран входа" },
      { type: "steps", items: [
        "Откройте адрес трекера и введите рабочую почту и пароль",
        "Если включена двухфакторная авторизация, введите шестизначный код из письма",
        "Забыли пароль — нажмите «Сбросить пароль»: на почту придёт код для установки нового",
      ] },
      { type: "tip", text: "Отметка «Запомнить меня» сохраняет вход на этом устройстве. На чужих компьютерах её лучше не ставить" },
      { type: "h", text: "Шапка" },
      { type: "list", items: [
        "Логотип — возврат на главный экран",
        "Кнопка языка — русский или английский интерфейс (если её не скрыл администратор)",
        "Кнопка «Справка» — это руководство",
        "Кнопка темы — светлая или тёмная тема",
        "Аватар — ваш аккаунт и выход",
      ] },
      { type: "list", roles: DIRECTORS, items: ["Шестерёнка — панель управления: пользователи, разделы и настройки"] },
    ],
  },
  {
    id: "roles",
    title: "Роли и права",
    roles: ALL,
    blocks: [
      { type: "p", text: "Что вам доступно, зависит от роли. Это руководство показывает только те возможности, которые есть у вашей роли" },
      { type: "roles" },
    ],
  },
  {
    id: "tasks-list",
    title: "Список задач",
    roles: ALL,
    blocks: [
      { type: "p", roles: ["executor"], text: "На главном экране — задачи, которые назначены вам. Над списком — фильтры и статистика" },
      { type: "p", roles: MANAGERS, text: "На главном экране — все задачи компании. Над списком — фильтры и статистика" },
      { type: "image", roles: ["executor"], src: "main-executor.webp", caption: "Главный экран исполнителя" },
      { type: "image", roles: MANAGERS, src: "main-manager.webp", caption: "Главный экран руководителя" },
      { type: "h", text: "Карточка задачи в списке" },
      { type: "list", items: [
        "Цветная плашка — статус: «Назначена», «В работе», «Выполнена», «Просрочена» или «В архиве»",
        "Рядом — разделы, к которым относится задача",
        "Справа — инициатор, исполнитель, дата назначения, срок и число комментариев",
        "Зелёный счётчик комментариев — в задаче есть сообщения, которые вы ещё не читали",
        "Красная рамка — срок прошёл, а задача не выполнена",
      ] },
      { type: "tip", text: "Нажмите на имя человека, чтобы открыть его профиль: должность и контакты" },
      { type: "h", text: "Фильтры" },
      { type: "image", src: "filters.webp", caption: "Панель фильтров" },
      { type: "list", items: [
        "«Разделы» — показать задачи только выбранных разделов. Можно выбрать несколько",
        "Зелёные кнопки — показать или скрыть назначенные, взятые в работу и выполненные задачи",
        "Оранжевая кнопка — показать задачи из архива",
        "Последняя кнопка — показать или скрыть статистику",
      ] },
      { type: "image", src: "sections-menu.webp", caption: "Выбор разделов. Раздел по умолчанию отмечен" },
      { type: "h", text: "Сортировка, группировка и поиск" },
      { type: "image", src: "sort-menu.webp", caption: "Меню сортировки" },
      { type: "list", items: [
        "Сортировка — по сроку, по исполнителю или сначала новые",
        "Группировка — по сроку или по исполнителю",
        "Поиск — по названию, описанию, людям и разделам",
      ] },
      { type: "tip", text: "Фильтры, сортировка и поиск запоминаются: в следующий раз список откроется в том же виде" },
    ],
  },
  {
    id: "task-work",
    title: "Работа с задачей",
    roles: ALL,
    blocks: [
      { type: "p", text: "Нажмите на задачу в списке, чтобы открыть её страницу. Вверху — название и инициатор, ниже — раздел, исполнитель, даты, статус, описание, вложения и чат" },
      { type: "image", src: "task-page.webp", caption: "Страница задачи" },
      { type: "h", text: "Статусы задачи" },
      { type: "list", items: [
        "Назначена — задача поставлена, исполнитель ещё не приступил",
        "В работе — исполнитель взял задачу",
        "Выполнена — работа закончена, к задаче приложено решение",
        "Просрочена — срок прошёл, а задача не выполнена",
        "В архиве — задача убрана из активных и не учитывается в статистике",
      ] },
      { type: "h", roles: ["executor"], text: "Взять задачу в работу" },
      { type: "h", roles: MANAGERS, text: "Как исполнитель берёт задачу в работу" },
      { type: "p", text: "У новой задачи исполнитель видит подсвеченную зелёную кнопку «Взять в работу». После подтверждения статус меняется, а в чат задачи добавляется запись об этом" },
      { type: "video", src: "take-in-work.webm", poster: "task-todo.webp", caption: "Взять задачу в работу" },
      { type: "h", text: "Завершение задачи" },
      { type: "p", text: "Когда работа сделана, нажмите кнопку с галочкой и опишите решение. Без решения задачу завершить нельзя. Решение появится на странице задачи под описанием и в чате" },
      { type: "video", src: "complete-task.webm", poster: "complete-dialog.webp", caption: "Завершение задачи с решением" },
      { type: "image", src: "task-done.webp", caption: "Выполненная задача с блоком «Решение»" },
      { type: "tip", text: "Если работу нужно доделать, у выполненной задачи есть кнопка «Вернуть в работу»" },
      { type: "h", text: "Чат задачи" },
      { type: "p", text: "Внизу страницы — обсуждение задачи. Сообщения разделены по дням, ваши сообщения справа. Здесь же видны системные записи: смена статуса, архив, решение" },
      { type: "image", src: "chat.webp", caption: "Чат задачи" },
      { type: "list", items: [
        "Enter — отправить, Shift+Enter — новая строка",
        "Скрепка — прикрепить файлы до 10 МБ; картинки показываются прямо в чате",
        "Наберите @ и начало имени, чтобы упомянуть коллегу",
      ] },
      { type: "video", src: "comment.webm", poster: "chat.webp", caption: "Сообщение с упоминанием коллеги" },
    ],
  },
  {
    id: "task-manage",
    title: "Постановка задач",
    roles: MANAGERS,
    blocks: [
      { type: "p", text: "Новую задачу создаёт кнопка «+» над списком задач" },
      { type: "video", src: "create-task.webm", poster: "task-form.webp", caption: "Создание задачи" },
      { type: "image", src: "task-form.webp", caption: "Форма новой задачи" },
      { type: "list", items: [
        "Название — коротко и по делу: «Заменить смесители в домиках №7–№9»",
        "Описание — подробности; доступны жирный, курсив, подчёркивание и списки",
        "Исполнитель — найдите сотрудника по имени или почте",
        "Разделы — минимум один. Раздел по умолчанию уже выбран",
        "Дата начала и срок. Срок действует до конца указанного дня",
        "Файлы — инструкции, фото, сметы",
      ] },
      { type: "p", roles: ["manager"], text: "Руководитель ставит задачи исполнителям" },
      { type: "p", roles: ["owner"], text: "Владелец ставит задачи администраторам, руководителям и исполнителям" },
      { type: "p", roles: ["admin"], text: "Администратор может назначить задачу любому сотруднику" },
      { type: "tip", text: "После сохранения новой задачи вы возвращаетесь к списку, а исполнитель получает уведомление на почту, если оно включено" },
      { type: "h", text: "Изменение, архив и удаление" },
      { type: "p", roles: ["manager"], text: "Руководитель может изменять, архивировать и удалять задачи, которые поставил сам" },
      { type: "p", roles: DIRECTORS, text: "Владелец и администратор могут изменять, архивировать и удалять любые задачи" },
      { type: "image", src: "task-manager.webp", caption: "Кнопки управления задачей: статус, архив, изменить, удалить" },
      { type: "list", items: [
        "Карандаш — изменить название, описание, исполнителя, разделы, срок и статус",
        "Архив — убрать завершённую задачу из активных. Из архива задачу можно вернуть",
        "Корзина — удалить задачу. Удалённые задачи попадают в корзину администратора",
      ] },
      { type: "h", text: "Действия с несколькими задачами" },
      { type: "p", text: "Нажмите на карточку правой кнопкой мыши (на телефоне — долгое нажатие) и выберите «Выбрать». Отметьте нужные задачи и отправьте их в архив или удалите разом" },
      { type: "image", src: "selection.webp", caption: "Выбор нескольких задач" },
    ],
  },
  {
    id: "account",
    title: "Мой аккаунт",
    roles: ALL,
    blocks: [
      { type: "p", text: "Нажмите на аватар в правом верхнем углу и выберите «Аккаунт»" },
      { type: "image", src: "account.webp", caption: "Настройки аккаунта" },
      { type: "list", items: [
        "Аватар — нажмите на круг, чтобы загрузить фото, и ещё раз, чтобы удалить",
        "Фамилия, имя, отчество, должность и описание видны коллегам в профиле",
        "Двухфакторная авторизация — при входе на почту будет приходить код подтверждения",
        "Пароль меняется через «Сбросить пароль» на экране входа",
      ] },
      { type: "tip", text: "Пока изменения не сохранены, в окне горит красная плашка. «Отмена» возвращает прежние значения" },
    ],
  },
  {
    id: "users",
    title: "Пользователи",
    roles: DIRECTORS,
    blocks: [
      { type: "p", text: "Панель управления → «Пользователи». Здесь список сотрудников, их роли и последний вход" },
      { type: "image", src: "admin-users.webp", caption: "Список пользователей" },
      { type: "list", items: [
        "«+» — добавить сотрудника: укажите ФИО, почту, роль и пароль либо отправьте приглашение на почту",
        "Роль меняется в выпадающем списке у сотрудника",
        "Карандаш — изменить ФИО, должность и описание",
        "Замок — задать новый пароль",
        "Корзина — отключить пользователя. Его задачи и комментарии сохраняются",
      ] },
      { type: "image", src: "admin-create-user.webp", caption: "Новый пользователь" },
      { type: "tip", roles: ["owner"], text: "Владелец не видит и не может изменять учётные записи администраторов" },
    ],
  },
  {
    id: "sections",
    title: "Разделы",
    roles: DIRECTORS,
    blocks: [
      { type: "p", text: "Разделы — это направления работы: «Гостевые домики», «Стройка: банный комплекс» и т. п. Каждая задача относится хотя бы к одному разделу" },
      { type: "image", src: "admin-sections.webp", caption: "Разделы" },
      { type: "list", items: [
        "«+» — новый раздел, карандаш — переименовать или изменить описание",
        "Переключатель «Раздел по умолчанию» в окне раздела: такой раздел сразу выбран в новой задаче. Он отмечен плашкой «По умолчанию»",
        "Удалить можно только раздел без задач",
      ] },
    ],
  },
  {
    id: "trash",
    title: "Удалённые задачи",
    roles: DIRECTORS,
    blocks: [
      { type: "p", text: "Удалённые задачи не пропадают сразу, а попадают сюда. Их можно восстановить вместе с чатом и файлами" },
      { type: "image", src: "admin-trash.webp", caption: "Удалённые задачи" },
      { type: "p", roles: ["admin"], text: "Администратор может удалить задачу навсегда или очистить корзину целиком. Это действие необратимо" },
    ],
  },
  {
    id: "settings",
    title: "Настройки системы",
    roles: ["admin"],
    blocks: [
      { type: "p", text: "Разделы панели управления, доступные только администратору" },
      { type: "h", text: "Основные" },
      { type: "image", src: "admin-general.webp", caption: "Основные настройки" },
      { type: "list", items: [
        "Язык и тема по умолчанию — для экрана входа и новых пользователей",
        "Кнопка смены языка — показать или скрыть её у всех",
      ] },
      { type: "h", text: "Интерфейс" },
      { type: "image", src: "admin-interface.webp", caption: "Настройки интерфейса" },
      { type: "p", text: "Включает и отключает у всех статистику задач и кнопки фильтров по статусам" },
      { type: "h", text: "Безопасность" },
      { type: "image", src: "admin-security.webp", caption: "Безопасность" },
      { type: "list", items: [
        "Разрешить «Запомнить меня» на экране входа",
        "Обязательная двухфакторная авторизация для всех",
        "Длительность обычной сессии",
      ] },
      { type: "h", text: "Почта, уведомления и шаблоны" },
      { type: "list", items: [
        "«Эл. почта» — параметры SMTP-сервера, проверка подключения и тестовое письмо",
        "«Уведомления» — какие письма отправлять: новая задача, смена статуса, приближение срока",
        "«Шаблоны уведомлений» — тема и текст писем. Подстановки: {title}, {due_at}, {link}, {code}, {email}",
      ] },
      { type: "image", src: "admin-templates.webp", caption: "Шаблоны уведомлений" },
    ],
  },
];

export const ROLE_MATRIX = [
  ["Видит задачи", "все", "все", "все", "свои"],
  ["Берёт в работу и завершает назначенные ему задачи", "да", "да", "да", "да"],
  ["Создаёт задачи", "да", "да", "да", "—"],
  ["Кому может поставить задачу", "всем", "админ., рук., исп.", "исполнителям", "—"],
  ["Изменяет и удаляет задачи", "любые", "любые", "свои", "—"],
  ["Управляет пользователями и разделами", "да", "да", "—", "—"],
  ["Восстанавливает удалённые задачи", "да", "да", "—", "—"],
  ["Настройки системы и очистка корзины", "да", "—", "—", "—"],
];

export function docsForRole(role) {
  const allowed = (item) => !item.roles || item.roles.includes(role);
  return DOC_SECTIONS.filter(allowed).map((section) => ({ ...section, blocks: section.blocks.filter(allowed) }));
}
